import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ProtectedDocumentsAPI, CategoriesAPI, ProtectedCategoriesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';

/* ── helpers ─────────────────────────────────────────────────────────── */

const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (s) => {
  if (!s) return '';
  const [y, m] = s.split('-');
  const months = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  return m ? `${months[+m - 1]} ${y}` : y;
};

const fileExt = (f) => (f || '').split('.').pop().toLowerCase();

/* ── small components ────────────────────────────────────────────────── */

function FileTypeBadge({ filename }) {
  const ext = fileExt(filename);
  const cfg = ext === 'pdf'
    ? { label: 'PDF', bg: 'rgba(176,48,48,.1)', color: 'var(--danger)' }
    : ext === 'doc' || ext === 'docx'
    ? { label: 'DOC', bg: 'rgba(61,107,122,.1)', color: 'var(--lake)' }
    : ext === 'ppt' || ext === 'pptx'
    ? { label: 'PPT', bg: 'rgba(214,126,44,.1)', color: '#d67e2c' }
    : ext === 'xls' || ext === 'xlsx'
    ? { label: 'XLS', bg: 'rgba(34,124,50,.1)', color: '#227c32' }
    : ext === 'jpg' || ext === 'jpeg'
    ? { label: 'JPG', bg: 'rgba(76,140,106,.1)', color: '#4c8c6a' }
    : ext === 'png'
    ? { label: 'PNG', bg: 'rgba(108,90,160,.1)', color: '#6c5aa0' }
    : { label: 'FIL', bg: 'rgba(44,74,46,.1)', color: 'var(--forest)' };
  return (
    <span style={{
      fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em',
      padding: '0.2rem 0.5rem', borderRadius: 3,
      background: cfg.bg, color: cfg.color
    }}>
      {cfg.label}
    </span>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 1000,
      background: type === 'error' ? 'var(--danger)' : 'var(--forest)',
      color: '#fff', padding: '0.85rem 1.25rem', borderRadius: 6,
      boxShadow: '0 4px 20px rgba(0,0,0,.25)',
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      fontSize: '0.88rem', fontFamily: 'Lato, sans-serif',
      animation: 'slideUp .22s ease'
    }}>
      <span>{type === 'error' ? '✕' : '✓'}</span>
      <span>{message}</span>
      <button onClick={onClose} style={{
        background: 'none', border: 'none', color: 'rgba(255,255,255,.7)',
        cursor: 'pointer', fontSize: '1rem', marginLeft: '0.25rem', padding: 0
      }}>×</button>
    </div>
  );
}

/* ── drop zone ───────────────────────────────────────────────────────── */

function DropZone({ file, onFile, onClear, onFileNameSuggest }) {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const sanitizeName = (name) => {
    return name
      .replace(/[åä]/gi, 'a')
      .replace(/[ÅÄ]/g, 'A')
      .replace(/[ö]/gi, 'o')
      .replace(/[Ö]/g, 'O');
  };

  const extractDate = (filename) => {
    const today = new Date().toISOString().slice(0, 7);
    
    // Try YYYY-MM-DD or YYYY-MM pattern
    const datePattern = /(\d{4})[-_](\d{2})(?:[-_](\d{2}))?/;
    const match = filename.match(datePattern);
    
    if (match) {
      return `${match[1]}-${match[2]}`;
    }
    
    // Try month names (Swedish)
    const months = {
      'januari': '01', 'jan': '01',
      'februari': '02', 'feb': '02',
      'mars': '03', 'mar': '03',
      'april': '04', 'apr': '04',
      'maj': '05',
      'juni': '06', 'jun': '06',
      'juli': '07', 'jul': '07',
      'augusti': '08', 'aug': '08',
      'september': '09', 'sep': '09',
      'oktober': '10', 'okt': '10',
      'november': '11', 'nov': '11',
      'december': '12', 'dec': '12'
    };
    
    const lowerFilename = filename.toLowerCase();
    const yearMatch = lowerFilename.match(/(\d{4})/);
    
    for (const [monthName, monthNum] of Object.entries(months)) {
      if (lowerFilename.includes(monthName)) {
        const year = yearMatch ? yearMatch[1] : new Date().getFullYear();
        return `${year}-${monthNum}`;
      }
    }
    
    return today;
  };

  const pick = (f) => {
    if (!f) return;
    // Size validation is handled by backend (5MB for public, 25MB for internal)
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['pdf','doc','docx','ppt','pptx','xls','xlsx','jpg','jpeg','png'].includes(ext)) { alert('Endast PDF, DOC, PPT, XLS, bilder (JPG, PNG)'); return; }
    
    // Suggest filename and date from file
    const baseName = f.name.replace(/\.[^/.]+$/, '');
    const suggestedName = sanitizeName(baseName);
    const suggestedDate = extractDate(f.name);
    
    onFile(f);
    if (onFileNameSuggest) {
      onFileNameSuggest({ name: suggestedName, date: suggestedDate });
    }
  };

  if (file) return (
    <div style={{
      border: '1.5px solid var(--forest)', borderRadius: 6,
      background: 'rgba(44,74,46,.04)', padding: '0.9rem 1.1rem',
      display: 'flex', alignItems: 'center', gap: '0.75rem'
    }}>
      <FileTypeBadge filename={file.name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)' }}>{fmtSize(file.size)}</div>
      </div>
      <button onClick={onClear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-soft)', fontSize: '1.1rem', padding: '0.2rem' }}>×</button>
    </div>
  );

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files[0]); }}
      onClick={() => inputRef.current?.click()}
      style={{
        border: `1.5px dashed ${dragging ? 'var(--forest)' : 'var(--border)'}`,
        borderRadius: 6, background: dragging ? 'rgba(44,74,46,.05)' : 'var(--cream)',
        padding: '1.75rem 1rem', textAlign: 'center', cursor: 'pointer',
        transition: 'border-color .15s, background .15s'
      }}
    >
      <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png" style={{ display: 'none' }}
        onChange={e => pick(e.target.files[0])} />
      <div style={{ fontSize: '1.75rem', opacity: 0.4, marginBottom: '0.4rem' }}>📄</div>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-mid)' }}>
        Dra hit eller <span style={{ color: 'var(--forest)', fontWeight: 600 }}>välj fil</span>
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)', marginTop: '0.2rem' }}>PDF, DOC, PPT, XLS, bilder · max 5 MB</div>
    </div>
  );
}

/* ── document form drawer ────────────────────────────────────────────── */

function DocumentDrawer({ open, onClose, onSave, categories, doc }) {
  const [form, setForm] = useState({ namn: '', kategori: '', datum: '', is_protected: false });
  const [file, setFile] = useState(null);
  const [urlMode, setUrlMode] = useState(false);
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [suggestedName, setSuggestedName] = useState('');
  const [suggestedDate, setSuggestedDate] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      namn: doc?.namn || '',
      kategori: doc?.kategori || categories[0]?.namn || '',
      datum: doc?.datum || new Date().toISOString().slice(0, 7),
      is_protected: doc?.is_protected || false
    });
    setFile(null);
    setUrl(doc?.fil_url || doc?.fil || '');
    setUrlMode(!!(doc?.fil_url || doc?.fil));
    setError('');
    setSuggestedName('');
    setSuggestedDate('');
  }, [open, doc, categories]);

  const handleFileNameSuggest = ({ name, date }) => {
    setSuggestedName(name);
    setSuggestedDate(date);
  };

  const applySuggestedName = () => {
    if (suggestedName) {
      setForm(f => ({ ...f, namn: suggestedName }));
      setSuggestedName('');
    }
  };

  const applySuggestedDate = () => {
    if (suggestedDate) {
      setForm(f => ({ ...f, datum: suggestedDate }));
      setSuggestedDate('');
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.namn.trim()) { setError('Namn är obligatoriskt'); return; }
    if (!form.kategori) { setError('Välj en kategori'); return; }
    if (!doc && !file && !url.trim()) { setError('Välj en fil eller ange en URL'); return; }

    setSaving(true);
    try {
      if (file) {
        const fd = new FormData();
        fd.append('namn', form.namn.trim());
        fd.append('kategori', form.kategori);
        fd.append('datum', form.datum);
        fd.append('is_protected', String(form.is_protected));
        fd.append('file', file);
        await onSave({ type: 'upload', fd }, doc?.id);
      } else {
        await onSave({ type: 'json', ...form, namn: form.namn.trim(), fil: url.trim() }, doc?.id);
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Något gick fel');
    } finally {
      setSaving(false);
    }
  };

  const label = { display: 'block', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-soft)', marginBottom: '0.4rem' };
  const input = { width: '100%', padding: '0.6rem 0.85rem', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.9rem', background: 'var(--white)', color: 'var(--text)', boxSizing: 'border-box' };
  const modeBtn = (active) => ({
    flex: 1, padding: '0.4rem', borderRadius: 4, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
    border: 'none', background: active ? 'var(--forest)' : 'transparent', color: active ? '#fff' : 'var(--text-soft)',
    transition: 'all .15s'
  });

  return (
    <>
      {open && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 200 }} />}

      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 440, maxWidth: '95vw',
        background: 'var(--white)', zIndex: 201, overflowY: 'auto',
        boxShadow: '-8px 0 40px rgba(0,0,0,.18)',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform .28s cubic-bezier(.4,0,.2,1)',
        display: 'flex', flexDirection: 'column'
      }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.2rem', color: 'var(--forest)', margin: 0 }}>
            {doc ? 'Redigera dokument' : 'Ladda upp dokument'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4rem', color: 'var(--text-soft)', padding: '0.2rem 0.4rem', borderRadius: 3 }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          {error && (
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(176,48,48,.08)', border: '1px solid rgba(176,48,48,.3)', borderRadius: 4, fontSize: '0.88rem', color: 'var(--danger)' }}>
              {error}
            </div>
          )}

          <div>
            <label style={label}>Namn</label>
            <input style={input} value={form.namn} onChange={e => set('namn', e.target.value)} placeholder="T.ex. Fornbobladet nr 1 · 2026" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={label}>Kategori</label>
              <select style={input} value={form.kategori} onChange={e => set('kategori', e.target.value)}>
                <option value="">Välj...</option>
                {categories.map(c => <option key={c.id} value={c.namn}>{c.namn}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Datum</label>
              <input style={input} type="month" value={form.datum} onChange={e => set('datum', e.target.value)} />
            </div>
          </div>

          <div>
            <label style={label}>Synlighet</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[{ v: false, label: '🌐 Publik', desc: 'Visas för alla' }, { v: true, label: '🔒 Intern', desc: 'Kräver inloggning' }].map(({ v, label: l, desc }) => (
                <button key={String(v)} type="button" onClick={() => set('is_protected', v)} style={{
                  flex: 1, padding: '0.6rem 0.5rem', borderRadius: 4, cursor: 'pointer', textAlign: 'left',
                  border: form.is_protected === v ? '2px solid var(--forest)' : '1px solid var(--border)',
                  background: form.is_protected === v ? 'rgba(44,74,46,.06)' : 'var(--white)',
                  transition: 'all .15s'
                }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-mid)' }}>{l}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)' }}>{desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={label}>Fil</label>
            {/* mode toggle */}
            <div style={{ display: 'flex', background: 'var(--cream)', borderRadius: 5, padding: '0.25rem', gap: '0.25rem', marginBottom: '0.75rem' }}>
              <button type="button" style={modeBtn(!urlMode)} onClick={() => { setUrlMode(false); setUrl(''); }}>⬆ Ladda upp fil</button>
              <button type="button" style={modeBtn(urlMode)} onClick={() => { setUrlMode(true); setFile(null); }}>🔗 Länka URL</button>
            </div>

            {urlMode ? (
              <input
                style={input}
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://... eller /dokument/fil.pdf"
              />
            ) : (
              <DropZone file={file} onFile={setFile} onClear={() => setFile(null)} onFileNameSuggest={handleFileNameSuggest} />
            )}

            {/* Suggested name/date buttons */}
            {!urlMode && (suggestedName || suggestedDate) && (
              <div style={{ marginTop: '0.6rem', padding: '0.6rem 0.75rem', background: 'rgba(44,74,46,.06)', border: '1px dashed var(--forest)', borderRadius: 4 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-mid)', marginBottom: '0.4rem' }}>
                  Hittade i filnamnet:
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {suggestedName && (
                    <button
                      type="button"
                      onClick={applySuggestedName}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        padding: '0.35rem 0.6rem',
                        background: 'var(--white)',
                        border: '1px solid var(--forest)',
                        borderRadius: 4,
                        fontSize: '0.75rem',
                        color: 'var(--forest)',
                        cursor: 'pointer'
                      }}
                    >
                      📄 Använd "{suggestedName.substring(0, 25)}{suggestedName.length > 25 ? '...' : ''}" som namn
                    </button>
                  )}
                  {suggestedDate && (
                    <button
                      type="button"
                      onClick={applySuggestedDate}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        padding: '0.35rem 0.6rem',
                        background: 'var(--white)',
                        border: '1px solid var(--lake)',
                        borderRadius: 4,
                        fontSize: '0.75rem',
                        color: 'var(--lake)',
                        cursor: 'pointer'
                      }}
                    >
                      📅 Använd {fmtDate(suggestedDate)} som datum
                    </button>
                  )}
                </div>
              </div>
            )}

            {doc && !file && !urlMode && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)', marginTop: '0.4rem' }}>
                Lämna tom för att behålla befintlig fil
              </div>
            )}
          </div>

          <div style={{ marginTop: 'auto', display: 'flex', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
            <button type="button" onClick={onClose} disabled={saving} className="btn btn-ghost" style={{ flex: 1 }}>
              Avbryt
            </button>
            <button type="submit" disabled={saving} className="btn btn-forest" style={{ flex: 2 }}>
              {saving ? 'Sparar...' : doc ? 'Spara ändringar' : 'Ladda upp'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/* ── category manager modal ──────────────────────────────────────────── */

function CategoryModal({ open, onClose, categories, onSave, isAdmin }) {
  const [local, setLocal] = useState([]);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocal([...categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
  }, [categories, open]);

  if (!open) return null;

  const add = () => {
    if (!newName.trim()) return;
    setLocal(l => [...l, { id: `tmp-${Date.now()}`, namn: newName.trim(), sort_order: l.length + 1 }]);
    setNewName('');
  };

  const remove = (id) => {
    if (!confirm('Ta bort kategori?')) return;
    setLocal(l => l.filter(c => c.id !== id));
  };

  const move = (idx, dir) => {
    const a = [...local];
    const swap = idx + dir;
    if (swap < 0 || swap >= a.length) return;
    [a[idx], a[swap]] = [a[swap], a[idx]];
    a.forEach((c, i) => { c.sort_order = i + 1; });
    setLocal(a);
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(local); } finally { setSaving(false); }
  };

  const inputStyle = { flex: 1, padding: '0.55rem 0.8rem', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.88rem' };
  const iconBtn = (onClick, title, color = 'var(--text-soft)') => ({
    background: 'none', border: 'none', cursor: 'pointer', padding: '0.3rem',
    color, borderRadius: 3, fontSize: '0.9rem', title
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'var(--white)', borderRadius: 8, width: 420, maxWidth: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.1rem', color: 'var(--forest)', margin: 0 }}>Kategorier</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: 'var(--text-soft)' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
          {isAdmin && (
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <input style={inputStyle} value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="Ny kategori..." onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} />
              <button type="button" onClick={add} className="btn btn-forest" style={{ whiteSpace: 'nowrap' }}>+ Lägg till</button>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {local.map((cat, i) => (
              <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'var(--cream)', borderRadius: 4 }}>
                {isAdmin && editId === cat.id ? (
                  <input style={{ ...inputStyle, flex: 1 }} defaultValue={cat.namn}
                    autoFocus onBlur={e => { setLocal(l => l.map(c => c.id === cat.id ? { ...c, namn: e.target.value } : c)); setEditId(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
                ) : (
                  <span style={{ flex: 1, fontSize: '0.88rem', cursor: isAdmin ? 'text' : 'default' }}
                    onClick={() => isAdmin && setEditId(cat.id)}>{cat.namn}</span>
                )}
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '0.15rem' }}>
                    <button onClick={() => move(i, -1)} disabled={i === 0} style={{ ...iconBtn(), opacity: i === 0 ? 0.3 : 1 }}>↑</button>
                    <button onClick={() => move(i, 1)} disabled={i === local.length - 1} style={{ ...iconBtn(), opacity: i === local.length - 1 ? 0.3 : 1 }}>↓</button>
                    <button onClick={() => remove(cat.id)} style={iconBtn(null, 'Ta bort', 'var(--danger)')}>🗑</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' }}>
          <button onClick={onClose} className="btn btn-ghost">Stäng</button>
          {isAdmin && <button onClick={handleSave} disabled={saving} className="btn btn-forest">{saving ? 'Sparar...' : 'Spara'}</button>}
        </div>
      </div>
    </div>
  );
}

/* ── sync modal ─────────────────────────────────────────────────────────── */

function SyncModal({ open, onClose, bucket, isProtected, onSync }) {
  const [step, setStep] = useState('scanning'); // 'scanning', 'review', 'syncing', 'done', 'error'
  const [orphaned, setOrphaned] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const bucketLabel = bucket === 'documents-public' ? 'publika' : 'interna';
  const bucketIcon = bucket === 'documents-public' ? '🌐' : '🔒';

  useEffect(() => {
    if (!open) {
      setStep('scanning');
      setOrphaned([]);
      setSelected(new Set());
      setResults([]);
      setError('');
      return;
    }

    // Start scanning when modal opens
    scanBucket();
  }, [open]);

  const scanBucket = async () => {
    setStep('scanning');
    setLoading(true);
    try {
      const response = await ProtectedDocumentsAPI.syncScan(bucket);
      setOrphaned(response.orphaned || []);
      // Pre-select all files
      setSelected(new Set((response.orphaned || []).map(f => f.path)));
      setStep(response.orphaned?.length > 0 ? 'review' : 'done');
    } catch (err) {
      setError(err.message || 'Kunde inte skanna bucket');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const toggleFile = (path) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === orphaned.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(orphaned.map(f => f.path)));
    }
  };

  const handleSync = async () => {
    const filesToSync = orphaned.filter(f => selected.has(f.path));
    if (filesToSync.length === 0) return;
    
    // Warn if trying to sync too many files
    const MAX_SYNC_FILES = 20;
    if (filesToSync.length > MAX_SYNC_FILES) {
      setError(`Du kan synkronisera max ${MAX_SYNC_FILES} filer åt gången. Välj färre filer.`);
      return;
    }

    setStep('syncing');
    setLoading(true);
    try {
      const response = await ProtectedDocumentsAPI.syncConfirm(bucket, filesToSync);
      setResults(response.results || []);
      setStep('done');
      onSync(); // Refresh document list
    } catch (err) {
      setError(err.message || 'Synkronisering misslyckades');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'var(--white)', borderRadius: 8, width: 560, maxWidth: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
        
        {/* Header */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.1rem', color: 'var(--forest)', margin: 0 }}>
            Synkronisera {bucketLabel} dokument
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: 'var(--text-soft)' }}>×</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
          
          {/* Scanning step */}
          {step === 'scanning' && (
            <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem', animation: 'spin 1s linear infinite' }}>🔄</div>
              <div style={{ fontSize: '0.95rem', color: 'var(--text-mid)' }}>
                Skannar {bucketIcon} {bucketLabel} bucket...
              </div>
              <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
              `}</style>
            </div>
          )}

          {/* Review step */}
          {step === 'review' && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.88rem', color: 'var(--text-mid)', marginBottom: '0.5rem' }}>
                  Hittade {orphaned.length} filer som inte finns i databasen. 
                  Välj vilka som ska synkroniseras:
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem', color: 'var(--text-soft)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={selected.size === orphaned.length && orphaned.length > 0}
                      onChange={toggleAll}
                    />
                    Markera alla
                  </label>
                  <span>·</span>
                  <span>{selected.size} valda</span>
                  {selected.size > 20 && (
                    <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
                      (max 20 åt gången)
                    </span>
                  )}
                </div>
              </div>

              <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                {orphaned.map((file, idx) => (
                  <div 
                    key={file.path}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.75rem 1rem',
                      borderBottom: idx < orphaned.length - 1 ? '1px solid var(--border)' : 'none',
                      background: selected.has(file.path) ? 'rgba(44,74,46,.04)' : 'var(--white)'
                    }}
                  >
                    <input 
                      type="checkbox" 
                      checked={selected.has(file.path)}
                      onChange={() => toggleFile(file.path)}
                    />
                    <FileTypeBadge filename={file.name} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.88rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.name}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)' }}>
                        {fmtSize(file.size)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Syncing step */}
          {step === 'syncing' && (
            <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem', animation: 'spin 1s linear infinite' }}>🔄</div>
              <div style={{ fontSize: '0.95rem', color: 'var(--text-mid)' }}>
                Synkroniserar {selected.size} filer...
              </div>
              <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
              `}</style>
            </div>
          )}

          {/* Done step */}
          {step === 'done' && (
            <>
              {orphaned.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>✅</div>
                  <div style={{ fontSize: '0.95rem', color: 'var(--text-mid)' }}>
                    Inga filer hittades som behöver synkroniseras.
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)', marginTop: '0.5rem' }}>
                    Alla filer i {bucketIcon} {bucketLabel} bucket finns redan i databasen.
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>✅</div>
                    <div style={{ fontSize: '0.95rem', color: 'var(--text-mid)' }}>
                      Synkronisering slutförd!
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)', marginTop: '0.5rem' }}>
                      {results.filter(r => r.success).length} av {results.length} filer synkroniserades.
                    </div>
                  </div>
                  
                  {results.some(r => !r.success) && (
                    <div style={{ border: '1px solid var(--danger)', borderRadius: 6, padding: '1rem', background: 'rgba(176,48,48,.05)' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--danger)', marginBottom: '0.5rem' }}>
                        Misslyckade synkroniseringar:
                      </div>
                      {results.filter(r => !r.success).map((r, idx) => (
                        <div key={idx} style={{ fontSize: '0.78rem', color: 'var(--text-soft)', marginBottom: '0.25rem' }}>
                          • {r.name}: {r.error}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Error step */}
          {step === 'error' && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>❌</div>
              <div style={{ fontSize: '0.95rem', color: 'var(--danger)', marginBottom: '0.5rem' }}>
                Ett fel inträffade
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)' }}>
                {error}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' }}>
          {step === 'review' && (
            <>
              <button onClick={onClose} className="btn btn-ghost" disabled={loading}>Avbryt</button>
              <button 
                onClick={handleSync} 
                disabled={selected.size === 0 || loading} 
                className="btn btn-forest"
              >
                {loading ? 'Synkroniserar...' : `Synkronisera ${selected.size} filer`}
              </button>
            </>
          )}
          {(step === 'done' || step === 'error') && (
            <button onClick={onClose} className="btn btn-forest">Stäng</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── bulk upload modal ─────────────────────────────────────────────────── */

function BulkUploadModal({ open, onClose, bucket, onUpload }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [defaultDate, setDefaultDate] = useState(new Date().toISOString().slice(0, 7));
  const [defaultCategory, setDefaultCategory] = useState('Arkiv');
  const [isInternal, setIsInternal] = useState(bucket === 'documents-private');
  const fileInputRef = useRef();
  const dropZoneRef = useRef();

  const bucketLabel = isInternal ? 'interna' : 'publika';
  const bucketIcon = isInternal ? '🔒' : '🌐';

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setResults(null);
      setError('');
      return;
    }
    // Load categories and set defaults
    CategoriesAPI.getAll().then(c => {
      const cats = c.categories || [];
      setCategories(cats);
      if (cats.length > 0 && !cats.find(cat => cat.namn === 'Arkiv')) {
        setDefaultCategory(cats[0].namn);
      }
    });
    // Reset to initial bucket setting when opening
    setIsInternal(bucket === 'documents-private');
  }, [open, bucket]);

  // Sanitize filename
  const sanitizeName = (name) => {
    return name
      .replace(/[åä]/gi, 'a')
      .replace(/[ÅÄ]/g, 'A')
      .replace(/[ö]/gi, 'o')
      .replace(/[Ö]/g, 'O');
  };

  // Extract date from filename
  const extractDate = (filename) => {
    const today = new Date().toISOString().slice(0, 7);
    
    // Try YYYY-MM-DD or YYYY-MM pattern
    const datePattern = /(\d{4})[-_](\d{2})(?:[-_](\d{2}))?/;
    const match = filename.match(datePattern);
    
    if (match) {
      return `${match[1]}-${match[2]}`;
    }
    
    // Try month names (Swedish)
    const months = {
      'januari': '01', 'jan': '01',
      'februari': '02', 'feb': '02',
      'mars': '03', 'mar': '03',
      'april': '04', 'apr': '04',
      'maj': '05',
      'juni': '06', 'jun': '06',
      'juli': '07', 'jul': '07',
      'augusti': '08', 'aug': '08',
      'september': '09', 'sep': '09',
      'oktober': '10', 'okt': '10',
      'november': '11', 'nov': '11',
      'december': '12', 'dec': '12'
    };
    
    const lowerFilename = filename.toLowerCase();
    const yearMatch = lowerFilename.match(/(\d{4})/);
    
    for (const [monthName, monthNum] of Object.entries(months)) {
      if (lowerFilename.includes(monthName)) {
        const year = yearMatch ? yearMatch[1] : new Date().getFullYear();
        return `${year}-${monthNum}`;
      }
    }
    
    return today;
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList).map(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      
      // Try to extract date from filename, fall back to default date
      const extractedDate = extractDate(file.name);
      const fileDate = extractedDate !== new Date().toISOString().slice(0, 7) ? extractedDate : defaultDate;
      
      return {
        file,
        originalName: file.name,
        sanitizedName: sanitizeName(baseName),
        ext,
        size: file.size,
        namn: sanitizeName(baseName),
        kategori: defaultCategory,
        datum: fileDate,
        isProtected: isInternal
      };
    }).filter(f => ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png'].includes(f.ext));

    setFiles(prev => [...prev, ...newFiles].slice(0, 100)); // Max 100 files
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const updateFile = (index, field, value) => {
    setFiles(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError('');
    setResults({ uploading: true, current: 0, total: files.length, uploaded: 0, failed: 0 });

    let totalUploaded = 0;
    let totalFailed = 0;
    const allResults = [];

    try {
      // Process files one at a time: request URL → PUT directly to Supabase → register metadata
      for (let i = 0; i < files.length; i++) {
        const fileData = files[i];
        try {
          const urlInfo = await ProtectedDocumentsAPI.requestUploadUrl({
            fileName: fileData.file.name,
            is_protected: isInternal,
            namn: fileData.namn
          });

          const uploadResp = await fetch(urlInfo.signedUrl, {
            method: 'PUT',
            body: fileData.file,
            headers: { 'Content-Type': fileData.file.type }
          });

          if (!uploadResp.ok) throw new Error(`HTTP ${uploadResp.status}`);

          await ProtectedDocumentsAPI.registerDocument({
            docId: urlInfo.docId,
            path: urlInfo.path,
            bucket: urlInfo.bucket,
            namn: fileData.namn,
            kategori: fileData.kategori,
            datum: fileData.datum,
            is_protected: isInternal,
            fileName: fileData.file.name,
            fileSize: fileData.size
          });

          totalUploaded++;
          allResults.push({ name: fileData.originalName, success: true });
        } catch (err) {
          console.error(`Failed to upload ${fileData.originalName}:`, err);
          totalFailed++;
          allResults.push({ name: fileData.originalName, success: false, error: err.message });
        }

        setResults({
          uploading: true,
          current: i + 1,
          total: files.length,
          uploaded: totalUploaded,
          failed: totalFailed
        });
      }

      setResults({
        uploading: false,
        current: files.length,
        total: files.length,
        uploaded: totalUploaded,
        failed: totalFailed,
        results: allResults
      });

      if (totalUploaded > 0) {
        onUpload();
      }
    } catch (err) {
      setError(err.message || 'Uppladdning misslyckades');
      setResults(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'var(--white)', borderRadius: 8, width: 700, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
        
        {/* Header */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.1rem', color: 'var(--forest)', margin: 0 }}>
            📁 Massuppladdning av dokument
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: 'var(--text-soft)' }}>×</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
          
          {!results || results.uploading ? (
            <>
              {/* Default Settings */}
              <div style={{ 
                background: 'var(--cream)', 
                border: '1px solid var(--border)', 
                borderRadius: 6, 
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-mid)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Standardinställningar för nya filer
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  {/* Default Date */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '0.25rem' }}>
                      Standarddatum
                    </label>
                    <input
                      type="month"
                      value={defaultDate}
                      onChange={(e) => {
                        setDefaultDate(e.target.value);
                        // Update existing files that weren't manually edited
                        setFiles(prev => prev.map(f => f.datum === defaultDate ? { ...f, datum: e.target.value } : f));
                      }}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 3,
                        padding: '0.4rem 0.5rem',
                        fontSize: '0.8rem',
                        width: '100%'
                      }}
                    />
                  </div>
                  {/* Default Category */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-soft)', marginBottom: '0.25rem' }}>
                      Standardkategori
                    </label>
                    <select
                      value={defaultCategory}
                      onChange={(e) => {
                        setDefaultCategory(e.target.value);
                        // Update existing files that weren't manually edited
                        setFiles(prev => prev.map(f => f.kategori === defaultCategory ? { ...f, kategori: e.target.value } : f));
                      }}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 3,
                        padding: '0.4rem 0.5rem',
                        fontSize: '0.8rem',
                        width: '100%'
                      }}
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.namn}>{cat.namn}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Visibility Toggle */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setIsInternal(false);
                      setFiles(prev => prev.map(f => ({ ...f, isProtected: false })));
                    }}
                    style={{
                      flex: 1,
                      padding: '0.5rem 0.75rem',
                      border: `1px solid ${!isInternal ? 'var(--forest)' : 'var(--border)'}`,
                      borderRadius: 4,
                      background: !isInternal ? 'var(--forest)' : 'var(--white)',
                      color: !isInternal ? '#fff' : 'var(--text)',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.4rem'
                    }}
                  >
                    🌐 Publik
                  </button>
                  <button
                    onClick={() => {
                      setIsInternal(true);
                      setFiles(prev => prev.map(f => ({ ...f, isProtected: true })));
                    }}
                    style={{
                      flex: 1,
                      padding: '0.5rem 0.75rem',
                      border: `1px solid ${isInternal ? 'var(--lake)' : 'var(--border)'}`,
                      borderRadius: 4,
                      background: isInternal ? 'var(--lake)' : 'var(--white)',
                      color: isInternal ? '#fff' : 'var(--text)',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.4rem'
                    }}
                  >
                    🔒 Intern
                  </button>
                </div>
              </div>

              {/* Drop zone */}
              <div
                ref={dropZoneRef}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--border)',
                  borderRadius: 8,
                  padding: '2rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  marginBottom: '1rem',
                  background: 'var(--cream)'
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📂</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-mid)' }}>
                  Dra filer hit eller <span style={{ color: 'var(--forest)', fontWeight: 600 }}>klicka för att välja</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginTop: '0.25rem' }}>
                  Dokument & bilder · max 25MB per fil · max 100 filer
                </div>
              </div>

              {/* Error */}
              {error && (
                <div style={{ padding: '0.75rem', background: 'rgba(176,48,48,.08)', border: '1px solid var(--danger)', borderRadius: 4, marginBottom: '1rem', color: 'var(--danger)', fontSize: '0.85rem' }}>
                  {error}
                </div>
              )}

              {/* File count */}
              {files.length > 0 && (
                <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'var(--text-mid)' }}>
                  {files.length} filer valda
                </div>
              )}

              {/* File list */}
              {files.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                  {files.map((file, idx) => (
                    <div 
                      key={idx}
                       style={{
                        display: 'grid',
                        gridTemplateColumns: '40px 1fr 110px 95px 24px 32px',
                        gap: '0.5rem',
                        alignItems: 'center',
                        padding: '0.6rem 0.75rem',
                        borderBottom: idx < files.length - 1 ? '1px solid var(--border)' : 'none',
                        background: 'var(--white)',
                        fontSize: '0.78rem'
                      }}
                    >
                      <FileTypeBadge filename={file.originalName} />
                      
                      <input
                        type="text"
                        value={file.namn}
                        onChange={(e) => updateFile(idx, 'namn', e.target.value)}
                        placeholder="Filnamn..."
                        style={{
                          border: '1px solid var(--border)',
                          borderRadius: 3,
                          padding: '0.35rem 0.5rem',
                          fontSize: '0.78rem',
                          width: '100%'
                        }}
                      />
                      
                      <select
                        value={file.kategori}
                        onChange={(e) => updateFile(idx, 'kategori', e.target.value)}
                        style={{
                          border: '1px solid var(--border)',
                          borderRadius: 3,
                          padding: '0.35rem 0.25rem',
                          fontSize: '0.75rem'
                        }}
                      >
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.namn}>{cat.namn}</option>
                        ))}
                      </select>
                      
                      <input
                        type="month"
                        value={file.datum}
                        onChange={(e) => updateFile(idx, 'datum', e.target.value)}
                        style={{
                          border: '1px solid var(--border)',
                          borderRadius: 3,
                          padding: '0.35rem 0.25rem',
                          fontSize: '0.75rem'
                        }}
                      />
                      
                      {/* Visibility toggle per file */}
                      <button
                        onClick={() => updateFile(idx, 'isProtected', !file.isProtected)}
                        title={file.isProtected ? 'Intern fil (skyddad)' : 'Publik fil'}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          padding: '0.2rem',
                          opacity: 0.7
                        }}
                      >
                        {file.isProtected ? '🔒' : '🌐'}
                      </button>
                      
                      <button
                        onClick={() => removeFile(idx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--danger)',
                          fontSize: '1rem',
                          padding: '0.2rem'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Results */
            <div style={{ textAlign: 'center', padding: '1.5rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                {results.failed === 0 ? '✅' : '⚠️'}
              </div>
              <div style={{ fontSize: '1rem', color: 'var(--text-mid)', marginBottom: '0.5rem' }}>
                Uppladdning slutförd
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-soft)' }}>
                {results.uploaded} av {results.results?.length || 0} filer uppladdade
              </div>
              
              {results.failed > 0 && (
                <div style={{ marginTop: '1rem', textAlign: 'left', border: '1px solid var(--danger)', borderRadius: 6, padding: '1rem', background: 'rgba(176,48,48,.05)' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--danger)', marginBottom: '0.5rem' }}>
                    Misslyckade uppladdningar:
                  </div>
                  {results.results?.filter(r => !r.success).map((r, idx) => (
                    <div key={idx} style={{ fontSize: '0.75rem', color: 'var(--text-soft)' }}>
                      • {r.name}: {r.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' }}>
          {!results || results.uploading ? (
            <>
              <button onClick={onClose} className="btn btn-ghost" disabled={uploading}>Avbryt</button>
              {uploading && results?.uploading && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ 
                    flex: 1, 
                    height: '6px', 
                    background: 'var(--border)', 
                    borderRadius: 3,
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${(results.current / results.total) * 100}%`,
                      height: '100%',
                      background: 'var(--forest)',
                      borderRadius: 3,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-mid)', whiteSpace: 'nowrap' }}>
                    {results.current} / {results.total}
                  </span>
                </div>
              )}
              <button 
                onClick={handleUpload}
                disabled={files.length === 0 || uploading}
                className="btn btn-forest"
              >
                {uploading ? `Laddar upp...` : `Ladda upp ${files.length} filer`}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="btn btn-forest">Stäng</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── main component ──────────────────────────────────────────────────── */

export default function DocumentsManager() {
  const { hasRole } = useAuth();
  const canEdit = hasRole('contentCreator');
  const isAdmin = hasRole('admin');
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [tab, setTab] = useState('public');
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [showSyncPublic, setShowSyncPublic] = useState(false);
  const [showSyncPrivate, setShowSyncPrivate] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploadBucket, setBulkUploadBucket] = useState('documents-public');
  const [showShares, setShowShares] = useState(false);
  const [shares, setShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditCategory, setBulkEditCategory] = useState('');
  const [bulkEditDate, setBulkEditDate] = useState('');
  const [autoExtractDates, setAutoExtractDates] = useState(false);
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  const [bulkDeleteSaving, setBulkDeleteSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ active: false, current: 0, total: 0, operation: '' });
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const addDropdownRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([ProtectedDocumentsAPI.getAll(), CategoriesAPI.getAll()]);
      setDocuments(d.documents || []);
      setCategories(c.categories || []);
    } catch { notify('Kunde inte ladda data', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showAddDropdown) return;
    const handler = (e) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target)) {
        setShowAddDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAddDropdown]);

  const notify = (message, type = 'success') => setToast({ message, type });

  // Category order for sorting
  const catOrder = categories.map(c => c.namn);
  
  // Filter by tab, search, and category - sort by: 1) category order, 2) date (desc), 3) name
  const visible = documents
    .filter(d => tab === 'internal' ? (d.is_protected || d.isProtected) : !(d.is_protected || d.isProtected))
    .filter(d => !search || d.namn.toLowerCase().includes(search.toLowerCase()))
    .filter(d => !activeCategory || d.kategori === activeCategory)
    .sort((a, b) => {
      // First: by category order
      const catA = a.kategori || 'Övrigt';
      const catB = b.kategori || 'Övrigt';
      const catIndexA = catOrder.indexOf(catA);
      const catIndexB = catOrder.indexOf(catB);
      const catSort = (catIndexA === -1 ? 999 : catIndexA) - (catIndexB === -1 ? 999 : catIndexB);
      if (catSort !== 0) return catSort;
      
      // Second: by date (descending - newest first)
      const dateSort = (b.datum || '').localeCompare(a.datum || '');
      if (dateSort !== 0) return dateSort;
      
      // Third: by name (ascending - alphabetical)
      return (a.namn || '').localeCompare(b.namn || '');
    });

  const grouped = visible.reduce((acc, d) => {
    const k = d.kategori || 'Övrigt';
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});

  const openAdd = () => { setEditDoc(null); setShowDrawer(true); };
  const openEdit = (doc) => { setEditDoc(doc); setShowDrawer(true); };

  const handleSave = async (data, id) => {
    if (data.type === 'upload') {
      const fd = data.fd;
      const file = fd.get('file');
      const namn = fd.get('namn');
      const kategori = fd.get('kategori');
      const datum = fd.get('datum');
      const is_protected = fd.get('is_protected');

      if (id) {
        const urlInfo = await ProtectedDocumentsAPI.requestReplaceUrl(id, { fileName: file.name, is_protected });
        const uploadResp = await fetch(urlInfo.signedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        if (!uploadResp.ok) throw new Error('Uppladdning misslyckades');
        await ProtectedDocumentsAPI.registerReplacement(id, {
          path: urlInfo.path, oldPath: urlInfo.oldPath, oldBucket: urlInfo.oldBucket,
          namn, kategori, datum, is_protected, fileName: file.name, fileSize: file.size
        });
      } else {
        const urlInfo = await ProtectedDocumentsAPI.requestUploadUrl({ fileName: file.name, is_protected, namn });
        const uploadResp = await fetch(urlInfo.signedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        if (!uploadResp.ok) throw new Error('Uppladdning misslyckades');
        await ProtectedDocumentsAPI.registerDocument({
          docId: urlInfo.docId, path: urlInfo.path, bucket: urlInfo.bucket,
          namn, kategori, datum, is_protected, fileName: file.name, fileSize: file.size
        });
      }
    } else {
      const { type, ...json } = data;
      if (id) {
        await ProtectedDocumentsAPI.update(id, json);
      } else {
        await ProtectedDocumentsAPI.create(json);
      }
    }
    await load();
    notify(id ? 'Dokument uppdaterat' : 'Dokument sparat');
  };

  const handleDelete = async (id, namn) => {
    if (!confirm(`Ta bort "${namn}"?`)) return;
    try {
      await ProtectedDocumentsAPI.delete(id);
      await load();
      notify('Dokument borttaget');
    } catch { notify('Kunde inte ta bort', 'error'); }
  };

  const handleMove = async (id, makeProtected) => {
    try {
      const response = await ProtectedDocumentsAPI.move(id, makeProtected);
      await load();
      notify(response.message || (makeProtected ? 'Dokument flyttat till interna' : 'Dokument flyttat till publika'));
    } catch (err) {
      notify('Kunde inte flytta dokument', 'error');
    }
  };

  // Bulk selection handlers
  const toggleSelection = (id) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedDocs(new Set(visible.map(d => d.id)));
  };

  const deselectAll = () => {
    setSelectedDocs(new Set());
  };

  const openBulkEdit = () => {
    setBulkEditCategory('');
    setBulkEditDate('');
    setAutoExtractDates(false);
    setBulkEditSaving(false);
    setBulkDeleteSaving(false);
    setBulkProgress({ active: false, current: 0, total: 0, operation: '' });
    setShowBulkEdit(true);
  };

  // Extract date from filename (same logic as bulk upload)
  const extractDateFromFilename = (filename) => {
    const today = new Date().toISOString().slice(0, 7);
    const lowerFilename = filename.toLowerCase();
    
    // Try Swedish format: "6 december-25" or "6 dec - 25" (day month-year)
    // Pattern: day (1-31) + optional spaces + month name + optional spaces + dash + 2-digit year
    const swedishMonths = {
      'januari': '01', 'jan': '01',
      'februari': '02', 'feb': '02',
      'mars': '03', 'mar': '03',
      'april': '04', 'apr': '04',
      'maj': '05',
      'juni': '06', 'jun': '06',
      'juli': '07', 'jul': '07',
      'augusti': '08', 'aug': '08',
      'september': '09', 'sep': '09',
      'oktober': '10', 'okt': '10',
      'november': '11', 'nov': '11',
      'december': '12', 'dec': '12'
    };
    
    // Try to match "day month-year" or "day month - year" patterns
    // e.g., "6 december-25", "6 dec - 25", "15 november-24"
    const swedishDatePattern = /(\d{1,2})\s*([a-zåäö]+)\s*[-]?\s*(\d{2,4})/i;
    const swedishMatch = lowerFilename.match(swedishDatePattern);
    
    if (swedishMatch) {
      const day = parseInt(swedishMatch[1], 10);
      const monthName = swedishMatch[2].toLowerCase().trim();
      const yearPart = swedishMatch[3];
      
      // Find the month number
      const monthNum = swedishMonths[monthName];
      if (monthNum && day >= 1 && day <= 31) {
        // Convert 2-digit year to 4-digit (assume 20xx if < 50, 19xx if >= 50)
        let year;
        if (yearPart.length === 2) {
          const yearInt = parseInt(yearPart, 10);
          year = yearInt < 50 ? 2000 + yearInt : 1900 + yearInt;
        } else {
          year = parseInt(yearPart, 10);
        }
        return `${year}-${monthNum}`;
      }
    }
    
    // Try YYYY-MM-DD or YYYY-MM pattern
    const datePattern = /(\d{4})[-_](\d{2})(?:[-_](\d{2}))?/;
    const match = filename.match(datePattern);
    
    if (match) {
      return `${match[1]}-${match[2]}`;
    }
    
    // Try month names (Swedish) without day
    const yearMatch = lowerFilename.match(/(\d{4})/);
    
    for (const [monthName, monthNum] of Object.entries(swedishMonths)) {
      if (lowerFilename.includes(monthName)) {
        const year = yearMatch ? yearMatch[1] : new Date().getFullYear();
        return `${year}-${monthNum}`;
      }
    }
    
    return today;
  };

  const handleBulkEditSave = async () => {
    if (!bulkEditCategory && !bulkEditDate && !autoExtractDates) {
      notify('Välj kategori, datum eller auto-extrahera', 'error');
      return;
    }
    
    setBulkEditSaving(true);
    const selectedDocIds = Array.from(selectedDocs);
    const BATCH_SIZE = 20;
    
    try {
      setShowBulkEdit(false); // Close modal, processing happens in background
      setBulkProgress({ active: true, current: 0, total: selectedDocIds.length, operation: 'Uppdaterar' });
      
      let successCount = 0;
      let failCount = 0;
      
      // Process in batches of 20
      for (let i = 0; i < selectedDocIds.length; i += BATCH_SIZE) {
        const batch = selectedDocIds.slice(i, i + BATCH_SIZE);
        const currentBatch = Math.floor(i / BATCH_SIZE) + 1;
        
        try {
          if (autoExtractDates) {
            // Auto-extract mode: get documents and update individually
            const docsToUpdate = visible.filter(d => batch.includes(d.id));
            const batchResults = await Promise.all(
              docsToUpdate.map(async (doc) => {
                const filename = doc.fil_namn || doc.namn || '';
                const extractedDate = extractDateFromFilename(filename);
                
                const updates = {};
                if (bulkEditCategory) updates.kategori = bulkEditCategory;
                updates.datum = extractedDate;
                
                try {
                  await ProtectedDocumentsAPI.update(doc.id, updates);
                  return { success: true };
                } catch (err) {
                  return { success: false };
                }
              })
            );
            successCount += batchResults.filter(r => r.success).length;
            failCount += batchResults.filter(r => !r.success).length;
          } else {
            // Standard bulk update
            const updates = {};
            if (bulkEditCategory) updates.kategori = bulkEditCategory;
            if (bulkEditDate) updates.datum = bulkEditDate;
            
            const response = await ProtectedDocumentsAPI.bulkUpdate(batch, updates);
            successCount += response.updated || 0;
            failCount += (response.failed || 0);
          }
          
          // Update progress
          setBulkProgress({ 
            active: true, 
            current: Math.min((i + BATCH_SIZE), selectedDocIds.length), 
            total: selectedDocIds.length, 
            operation: 'Uppdaterar' 
          });
          
          // Small delay to allow UI to update and prevent overwhelming the server
          if (i + BATCH_SIZE < selectedDocIds.length) {
            await new Promise(r => setTimeout(r, 500));
          }
          
        } catch (err) {
          console.error(`Batch ${currentBatch} failed:`, err);
          failCount += batch.length;
        }
      }
      
      await load();
      setSelectedDocs(new Set());
      setBulkProgress({ active: false, current: 0, total: 0, operation: '' });
      
      const message = autoExtractDates 
        ? `${successCount} av ${selectedDocIds.length} dokument uppdaterade med extraherade datum${failCount > 0 ? ` (${failCount} misslyckade)` : ''}`
        : `${successCount} dokument uppdaterade${failCount > 0 ? ` (${failCount} misslyckade)` : ''}`;
      notify(message);
      
    } catch (err) {
      setBulkProgress({ active: false, current: 0, total: 0, operation: '' });
      notify('Kunde inte uppdatera dokument', 'error');
    } finally {
      setBulkEditSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Ta bort ${selectedDocs.size} markerade dokument permanent?`)) return;
    
    setBulkDeleteSaving(true);
    setShowBulkEdit(false);
    setBulkProgress({ active: true, current: 0, total: selectedDocs.size, operation: 'Tar bort' });
    
    try {
      const ids = Array.from(selectedDocs);
      const BATCH_SIZE = 20;
      let successCount = 0;
      let failCount = 0;
      
      // Process in batches of 20
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        const batch = ids.slice(i, i + BATCH_SIZE);
        
        try {
          await Promise.all(
            batch.map(async (id) => {
              try {
                await ProtectedDocumentsAPI.delete(id);
                return { success: true };
              } catch (err) {
                return { success: false };
              }
            })
          );
          successCount += batch.length;
        } catch (err) {
          console.error('Batch delete failed:', err);
          failCount += batch.length;
        }
        
        // Update progress
        setBulkProgress({ 
          active: true, 
          current: Math.min((i + BATCH_SIZE), ids.length), 
          total: ids.length, 
          operation: 'Tar bort' 
        });
        
        // Small delay between batches
        if (i + BATCH_SIZE < ids.length) {
          await new Promise(r => setTimeout(r, 500));
        }
      }
      
      await load();
      setSelectedDocs(new Set());
      setBulkProgress({ active: false, current: 0, total: 0, operation: '' });
      notify(`${successCount} dokument borttagna${failCount > 0 ? ` (${failCount} misslyckade)` : ''}`);
    } catch (err) {
      setBulkProgress({ active: false, current: 0, total: 0, operation: '' });
      notify('Kunde inte ta bort alla dokument', 'error');
    } finally {
      setBulkDeleteSaving(false);
    }
  };

  const handlePreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewLoading(true);
    setPreviewContent(null);
    
    try {
      const response = await ProtectedDocumentsAPI.download(doc.id);
      if (response.file) {
        const ext = (doc.fil_namn || doc.namn || '').split('.').pop().toLowerCase();
        
        // Convert base64 to blob URL
        const byteCharacters = atob(response.file);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: response.contentType });
        const url = window.URL.createObjectURL(blob);
        
        setPreviewContent({
          url,
          type: response.contentType,
          ext,
          filename: response.filename || doc.namn
        });
      }
    } catch (err) {
      console.error('Preview failed:', err);
      setPreviewContent({ error: 'Kunde inte ladda förhandsvisning' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewContent?.url) {
      window.URL.revokeObjectURL(previewContent.url);
    }
    setPreviewDoc(null);
    setPreviewContent(null);
    setPreviewLoading(false);
  };

  const handleShare = async (doc) => {
    try {
      const response = await ProtectedDocumentsAPI.generateShareLink(doc.id);
      if (response.success && response.shareUrl) {
        // Copy to clipboard
        await navigator.clipboard.writeText(response.shareUrl);
        notify(doc.is_protected ? `Delningslänk kopierad! Giltig i 60 dagar.` : `Delningslänk kopierad! Giltig för alltid.`);
      } else {
        notify('Kunde inte skapa delningslänk', 'error');
      }
    } catch (err) {
      console.error('Share link generation failed:', err);
      notify('Kunde inte skapa delningslänk', 'error');
    }
  };

  const handleSaveCats = async (cats) => {
    const origIds = categories.map(c => c.id);
    const newIds = cats.filter(c => !c.id.startsWith('tmp-')).map(c => c.id);
    await Promise.all([
      ...origIds.filter(id => !newIds.includes(id)).map(id => ProtectedCategoriesAPI.delete(id)),
      ...cats.filter(c => !c.id.startsWith('tmp-')).map(c => ProtectedCategoriesAPI.update(c.id, { namn: c.namn, sort_order: c.sort_order })),
      ...cats.filter(c => c.id.startsWith('tmp-')).map(({ id, ...rest }) => ProtectedCategoriesAPI.create(rest))
    ]);
    await load();
    setShowCats(false);
    notify('Kategorier sparade');
  };

  const loadShares = async () => {
    setSharesLoading(true);
    try {
      const response = await ProtectedDocumentsAPI.getShares();
      setShares(response.shares || []);
    } catch (err) {
      console.error('Load shares error:', err);
      notify('Kunde inte ladda delningslänkar', 'error');
    } finally {
      setSharesLoading(false);
    }
  };

  const handleDeleteShare = async (shareId) => {
    try {
      await ProtectedDocumentsAPI.deleteShare(shareId);
      setShares(prev => prev.filter(s => s.id !== shareId));
      notify('Delningslänk borttagen');
    } catch (err) {
      notify('Kunde inte ta bort delningslänk', 'error');
    }
  };

  const tabStyle = (active) => ({
    padding: '0.6rem 1.1rem', background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
    color: active ? 'var(--forest)' : 'var(--text-soft)',
    borderBottom: active ? '2px solid var(--forest)' : '2px solid transparent',
    transition: 'color .15s, border-color .15s'
  });

  // Groups are already sorted by the visible sort (category -> date -> name)
  const sortedGroups = Object.entries(grouped);

  // Filter by category if selected
  const filteredByCat = activeCategory ? { [activeCategory]: grouped[activeCategory] || [] } : grouped;
  const displayGroups = activeCategory ? Object.entries(filteredByCat) : sortedGroups;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--off-white, #f7f6f2)' }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* ── hero header ── */}
      <div style={{ background: 'var(--forest)', color: '#fff', padding: '3rem 1.5rem 2.5rem' }}>
        <div className="container" style={{ maxWidth: 900 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <Link to="/styrelsen/dashboard" style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                fontSize: '0.78rem', color: 'rgba(255,255,255,.65)', textDecoration: 'none',
                letterSpacing: '0.04em', marginBottom: '1.25rem'
              }}>
                ← Dashboard
              </Link>
              <h1 style={{ fontFamily: '"Playfair Display", serif', fontSize: '2.2rem', margin: '0 0 0.5rem', fontWeight: 700 }}>
                Dokumenthantering
              </h1>
              <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,.75)', margin: 0, maxWidth: 520 }}>
                Hantera föreningens dokument. Ladda upp nya filer, organisera i kategorier och hantera åtkomst för interna dokument.
              </p>
            </div>
            <div style={{ display: 'flex', gap: isMobile ? '0.4rem' : '0.6rem', flexWrap: 'wrap', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
              {isAdmin && !isMobile && (
                <>
                  <button onClick={() => setShowSyncPublic(true)} className="btn btn-ghost" style={{ 
                    fontSize: '0.78rem', 
                    borderColor: 'rgba(255,255,255,.5)', 
                    color: '#fff',
                    background: 'rgba(255,255,255,.1)'
                  }}>
                    🔄 Synkronisera publika
                  </button>
                  <button onClick={() => setShowSyncPrivate(true)} className="btn btn-ghost" style={{ 
                    fontSize: '0.78rem', 
                    borderColor: 'rgba(255,255,255,.5)', 
                    color: '#fff',
                    background: 'rgba(255,255,255,.1)'
                  }}>
                    🔄 Synkronisera interna
                  </button>
                </>
              )}
              {isAdmin && (
                <button onClick={() => setShowCats(true)} className="btn btn-ghost" style={{ 
                  fontSize: isMobile ? '0.7rem' : '0.78rem', 
                  borderColor: 'rgba(255,255,255,.5)', 
                  color: '#fff',
                  background: 'rgba(255,255,255,.1)',
                  padding: isMobile ? '0.35rem 0.6rem' : '0.5rem 1rem',
                  whiteSpace: 'nowrap'
                }}>
                  {isMobile ? '⚙ Kategorier' : '⚙ Hantera kategorier'}
                </button>
              )}
              {canEdit && (
                <button 
                  onClick={() => { setShowShares(true); loadShares(); }}
                  className="btn btn-ghost" 
                  style={{ 
                    fontSize: isMobile ? '0.7rem' : '0.78rem', 
                    borderColor: 'rgba(255,255,255,.5)', 
                    color: '#fff',
                    background: 'rgba(255,255,255,.1)',
                    padding: isMobile ? '0.35rem 0.6rem' : '0.5rem 1rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {isMobile ? '🔗 Delningslänkar' : '🔗 Aktiva delningslänkar'}
                </button>
              )}
              {canEdit && (
                <div ref={addDropdownRef} style={{ position: 'relative' }}>
                  <button 
                    onClick={() => setShowAddDropdown(!showAddDropdown)}
                    className="btn btn-forest" 
                    style={{ 
                      fontSize: '0.78rem',
                      background: '#fff',
                      color: 'var(--forest)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem'
                    }}
                  >
                    + Nytt dokument ▼
                  </button>
                  {showAddDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '0.25rem',
                      background: '#fff',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      boxShadow: '0 4px 12px rgba(0,0,0,.15)',
                      zIndex: 100,
                      minWidth: 180
                    }}>
                      <button
                        onClick={() => { setShowAddDropdown(false); openAdd(); }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          width: '100%',
                          padding: '0.6rem 0.85rem',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          fontSize: '0.82rem',
                          textAlign: 'left',
                          color: 'var(--text)'
                        }}
                        onMouseEnter={e => e.target.style.background = 'var(--cream)'}
                        onMouseLeave={e => e.target.style.background = 'transparent'}
                      >
                        📄 Enstaka fil
                      </button>
                      <button
                        onClick={() => { 
                          setShowAddDropdown(false); 
                          setBulkUploadBucket('documents-public');
                          setShowBulkUpload(true); 
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          width: '100%',
                          padding: '0.6rem 0.85rem',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          fontSize: '0.82rem',
                          textAlign: 'left',
                          color: 'var(--text)',
                          borderTop: '1px solid var(--border)'
                        }}
                        onMouseEnter={e => e.target.style.background = 'var(--cream)'}
                        onMouseLeave={e => e.target.style.background = 'transparent'}
                      >
                        📁 Flera filer (max 100)
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── filter bar ── */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem', position: 'sticky', top: 0, zIndex: 10 }}>
        <div className="container" style={{ maxWidth: 900, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
          
          {/* search */}
          <div style={{ position: 'relative', flexShrink: 0, flexGrow: 1, minWidth: 160, maxWidth: 280 }}>
            <span style={{
              position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-soft)', fontSize: '0.85rem', pointerEvents: 'none'
            }}>⌕</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Sök dokument..."
              style={{
                width: '100%', padding: '0.5rem 0.75rem 0.5rem 2rem',
                border: '1px solid var(--border)', borderRadius: 4,
                fontSize: '0.88rem', background: 'var(--cream)', color: 'var(--text)',
                outline: 'none'
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{
                position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-soft)', fontSize: '1rem', lineHeight: 1, padding: '0.1rem'
              }}>×</button>
            )}
          </div>

          {/* visibility tabs */}
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              onClick={() => setTab('public')}
              style={{
                padding: '0.35rem 0.85rem', borderRadius: 20, border: 'none',
                fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                letterSpacing: '0.03em',
                background: tab === 'public' ? 'var(--forest)' : 'var(--cream)',
                color: tab === 'public' ? '#fff' : 'var(--text-soft)',
                transition: 'background .15s, color .15s'
              }}
            >
              🌐 Publika
            </button>
            <button
              onClick={() => setTab('internal')}
              style={{
                padding: '0.35rem 0.85rem', borderRadius: 20, border: 'none',
                fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                letterSpacing: '0.03em',
                background: tab === 'internal' ? 'var(--forest)' : 'var(--cream)',
                color: tab === 'internal' ? '#fff' : 'var(--text-soft)',
                transition: 'background .15s, color .15s'
              }}
            >
              🔒 Interna
            </button>
          </div>

          {/* category pills */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {[{ id: '', namn: 'Alla' }, ...categories].map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id === '' ? '' : cat.namn)}
                style={{
                  padding: '0.35rem 0.85rem', borderRadius: 20, border: 'none',
                  fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                  letterSpacing: '0.03em',
                  background: activeCategory === (cat.id === '' ? '' : cat.namn)
                    ? 'var(--forest)' : 'var(--cream)',
                  color: activeCategory === (cat.id === '' ? '' : cat.namn)
                    ? '#fff' : 'var(--text-soft)',
                  transition: 'background .15s, color .15s'
                }}
              >
                {cat.namn}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── content ── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1.5rem' }}>
        
        {/* Bulk selection toolbar */}
        {canEdit && visible.length > 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            background: 'var(--cream)',
            borderRadius: 6,
            marginBottom: '1rem',
            border: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={selectAll}
                className="btn btn-ghost"
                style={{ fontSize: '0.78rem', padding: '0.4rem 0.75rem' }}
              >
                Markera alla
              </button>
              <button
                onClick={deselectAll}
                className="btn btn-ghost"
                style={{ fontSize: '0.78rem', padding: '0.4rem 0.75rem' }}
              >
                Avmarkera
              </button>
            </div>
            
            {selectedDocs.size > 0 && !bulkProgress.active && (
              <>
                <div style={{ 
                  fontSize: '0.78rem', 
                  color: 'var(--forest)', 
                  fontWeight: 600,
                  padding: '0 0.5rem'
                }}>
                  {selectedDocs.size} markerade
                </div>
                <div style={{ flex: 1 }} />
                <button
                  onClick={openBulkEdit}
                  className="btn btn-forest"
                  style={{ fontSize: '0.78rem', padding: '0.4rem 0.85rem' }}
                >
                  ✏️ Ändra kategori/datum
                </button>
              </>
            )}
            
            {/* Progress indicator for bulk operations */}
            {bulkProgress.active && (
              <>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--forest)', fontWeight: 600 }}>
                      {bulkProgress.operation}...
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-mid)' }}>
                      {bulkProgress.current} / {bulkProgress.total}
                    </span>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '6px', 
                    background: 'var(--border)', 
                    borderRadius: 3,
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${(bulkProgress.current / bulkProgress.total) * 100}%`,
                      height: '100%',
                      background: 'var(--forest)',
                      borderRadius: 3,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        
        {/* result summary */}
        {!loading && (
          <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)', marginBottom: '1.25rem', letterSpacing: '0.02em' }}>
            {visible.length === 0
              ? 'Inga dokument'
              : `${visible.length} ${tab === 'internal' ? 'interna' : 'publika'} dokument${activeCategory ? ` i ${activeCategory}` : ''}${search ? ` · "${search}"` : ''}`}
          </div>
        )}

        {loading ? (
          <div className="loading">Laddar dokument...</div>
        ) : visible.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '5rem 2rem', color: 'var(--text-soft)' }}>
            <div style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '0.75rem' }}>📁</div>
            <div style={{ fontSize: '0.95rem', color: 'var(--text-mid)', marginBottom: '0.4rem' }}>
              Inga {tab === 'internal' ? 'interna' : 'publika'} dokument hittades
            </div>
            {(search || activeCategory) && (
              <button
                onClick={() => { setSearch(''); setActiveCategory(''); }}
                className="btn btn-ghost"
                style={{ marginTop: '0.75rem', fontSize: '0.78rem' }}
              >
                Rensa filter
              </button>
            )}
            {canEdit && !search && !activeCategory && (
              <button onClick={openAdd} className="btn btn-forest" style={{ marginTop: '1rem', fontSize: '0.78rem' }}>
                + Lägg till första dokumentet
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
            {displayGroups.map(([cat, docs]) => (
              <section key={cat}>
                {/* category header */}
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: '0.6rem',
                  marginBottom: '0.75rem', paddingBottom: '0.5rem',
                  borderBottom: '2px solid var(--forest)'
                }}>
                  <h2 style={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '1.1rem', color: 'var(--forest)', margin: 0
                  }}>
                    {cat}
                  </h2>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-soft)', fontWeight: 400 }}>
                    {docs.length} {docs.length === 1 ? 'dokument' : 'dokument'}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '0.6rem' : '0.45rem' }}>
                  {docs.map(doc => (
                    <DocRow 
                      key={doc.id} 
                      doc={doc} 
                      onEdit={openEdit} 
                      onDelete={handleDelete} 
                      onMove={handleMove}
                      onPreview={handlePreview}
                      onShare={handleShare}
                      canEdit={canEdit}
                      isSelected={selectedDocs.has(doc.id)}
                      onSelect={toggleSelection}
                      selectionMode={selectedDocs.size > 0}
                      isMobile={isMobile}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      <DocumentDrawer open={showDrawer && canEdit} onClose={() => setShowDrawer(false)} onSave={handleSave} categories={categories} doc={editDoc} />
      <CategoryModal open={showCats} onClose={() => setShowCats(false)} categories={categories} onSave={handleSaveCats} isAdmin={isAdmin} />
      <SyncModal 
        open={showSyncPublic} 
        onClose={() => setShowSyncPublic(false)} 
        bucket="documents-public"
        isProtected={false}
        onSync={load}
      />
      <SyncModal 
        open={showSyncPrivate} 
        onClose={() => setShowSyncPrivate(false)} 
        bucket="documents-private"
        isProtected={true}
        onSync={load}
      />
      <BulkUploadModal
        open={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        bucket={bulkUploadBucket}
        onUpload={load}
      />
      
      {/* Bulk Edit Modal */}
      {showBulkEdit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--white)', borderRadius: 8, width: 420, maxWidth: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.1rem', color: 'var(--forest)', margin: 0 }}>
                ✏️ Ändra {selectedDocs.size} dokument
              </h3>
              <button onClick={() => setShowBulkEdit(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.3rem', color: 'var(--text-soft)' }}>×</button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-soft)', marginBottom: '0.4rem' }}>
                  Ny kategori (lämna tom för att inte ändra)
                </label>
                <select
                  value={bulkEditCategory}
                  onChange={e => setBulkEditCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.85rem',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    fontSize: '0.9rem',
                    background: 'var(--white)'
                  }}
                >
                  <option value="">-- Behåll nuvarande --</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.namn}>{c.namn}</option>
                  ))}
                </select>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-soft)', marginBottom: '0.4rem' }}>
                  Nytt datum (lämna tom för att inte ändra)
                </label>
                <input
                  type="month"
                  value={bulkEditDate}
                  onChange={e => {
                    setBulkEditDate(e.target.value);
                    if (e.target.value) setAutoExtractDates(false);
                  }}
                  disabled={autoExtractDates}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.85rem',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    fontSize: '0.9rem',
                    background: autoExtractDates ? 'var(--cream)' : 'var(--white)',
                    opacity: autoExtractDates ? 0.6 : 1
                  }}
                />
              </div>
              
              {/* Auto-extract dates from filenames */}
              <div style={{ 
                marginBottom: isAdmin ? '1rem' : 0,
                padding: '0.75rem',
                background: 'rgba(44,74,46,.05)',
                border: '1px solid rgba(44,74,46,.15)',
                borderRadius: 6
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setAutoExtractDates(!autoExtractDates);
                    if (!autoExtractDates) setBulkEditDate('');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    background: autoExtractDates ? 'var(--forest)' : 'var(--white)',
                    border: `1px solid ${autoExtractDates ? 'var(--forest)' : 'var(--border)'}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: autoExtractDates ? '#fff' : 'var(--text)'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>📅</span>
                    <span>Extrahera datum från filnamn</span>
                  </span>
                  <span style={{ 
                    width: '2.2rem',
                    height: '1.2rem',
                    borderRadius: '0.6rem',
                    background: autoExtractDates ? '#fff' : 'var(--border)',
                    position: 'relative',
                    transition: 'background .15s'
                  }}>
                    <span style={{
                      position: 'absolute',
                      top: '0.1rem',
                      left: autoExtractDates ? '1.1rem' : '0.1rem',
                      width: '1rem',
                      height: '1rem',
                      borderRadius: '50%',
                      background: autoExtractDates ? 'var(--forest)' : '#fff',
                      transition: 'left .15s'
                    }} />
                  </span>
                </button>
                <div style={{ 
                  fontSize: '0.72rem', 
                  color: 'var(--text-soft)', 
                  marginTop: '0.5rem',
                  fontStyle: 'italic'
                }}>
                  Analyserar datum i format: ÅÅÅÅ-MM, svenska månadsnamn
                </div>
                
                {/* Preview extracted dates */}
                {autoExtractDates && selectedDocs.size > 0 && (
                  <div style={{ 
                    marginTop: '0.75rem',
                    padding: '0.5rem',
                    background: 'var(--white)',
                    borderRadius: 4,
                    maxHeight: '120px',
                    overflowY: 'auto'
                  }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)', marginBottom: '0.35rem', fontWeight: 600 }}>
                      Förhandsvisning (extraherade datum):
                    </div>
                    {visible
                      .filter(d => selectedDocs.has(d.id))
                      .slice(0, 5)
                      .map(doc => {
                        const filename = doc.fil_namn || doc.namn || '';
                        const extractedDate = extractDateFromFilename(filename);
                        return (
                          <div key={doc.id} style={{ 
                            fontSize: '0.72rem', 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            padding: '0.2rem 0'
                          }}>
                            <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60%' }}>
                              {filename.substring(0, 30)}{filename.length > 30 ? '...' : ''}
                            </span>
                            <span style={{ color: 'var(--forest)', fontWeight: 600 }}>
                              → {fmtDate(extractedDate)}
                            </span>
                          </div>
                        );
                      })}
                    {selectedDocs.size > 5 && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)', fontStyle: 'italic', marginTop: '0.3rem' }}>
                        ... och {selectedDocs.size - 5} till
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Admin-only delete section */}
              {isAdmin && (
                <div style={{ 
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: 'rgba(176,48,48,.05)',
                  border: '1px solid rgba(176,48,48,.2)',
                  borderRadius: 6
                }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--danger)', marginBottom: '0.5rem', fontWeight: 600 }}>
                    ⚠️ Administratör: Ta bort valda dokument
                  </div>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkDeleteSaving}
                    className="btn"
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      background: 'var(--danger)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    {bulkDeleteSaving ? 'Tar bort...' : `🗑 Ta bort ${selectedDocs.size} dokument`}
                  </button>
                </div>
              )}
            </div>
            
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' }}>
              <button onClick={() => setShowBulkEdit(false)} className="btn btn-ghost" disabled={bulkEditSaving || bulkDeleteSaving}>Avbryt</button>
              <button 
                onClick={handleBulkEditSave}
                disabled={bulkEditSaving || bulkDeleteSaving || (!bulkEditCategory && !bulkEditDate && !autoExtractDates)}
                className="btn btn-forest"
              >
                {bulkEditSaving ? 'Sparar...' : (autoExtractDates ? 'Extrahera & uppdatera' : 'Uppdatera')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Preview Modal */}
      {previewDoc && (
        <div 
          onClick={closePreview}
          style={{ 
            position: 'fixed', 
            inset: 0, 
            background: 'rgba(0,0,0,.7)', 
            zIndex: 400, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '2rem'
          }}
        >
          <div 
            onClick={e => e.stopPropagation()}
            style={{ 
              background: 'var(--white)', 
              borderRadius: 8, 
              width: '90vw', 
              maxWidth: 1200,
              height: '90vh',
              display: 'flex', 
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,.4)',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <div style={{ 
              padding: '1rem 1.5rem', 
              borderBottom: '1px solid var(--border)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              background: 'var(--forest)',
              color: '#fff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                <FileTypeBadge filename={previewDoc.fil_namn || previewDoc.fil} />
                <h3 style={{ 
                  fontFamily: '"Playfair Display", serif', 
                  fontSize: '1.1rem', 
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {previewDoc.namn}
                </h3>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    // Download the file
                    const link = document.createElement('a');
                    link.href = previewContent?.url;
                    link.download = previewContent?.filename || previewDoc.namn;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  disabled={!previewContent?.url || previewLoading}
                  style={{
                    background: 'rgba(255,255,255,.2)',
                    border: 'none',
                    color: '#fff',
                    padding: '0.5rem 1rem',
                    borderRadius: 4,
                    cursor: previewContent?.url ? 'pointer' : 'not-allowed',
                    fontSize: '0.85rem',
                    opacity: previewContent?.url ? 1 : 0.5
                  }}
                >
                  ⬇ Ladda ner
                </button>
                <button 
                  onClick={closePreview}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    cursor: 'pointer', 
                    fontSize: '1.5rem', 
                    color: '#fff',
                    padding: '0.25rem',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto',
              background: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem'
            }}>
              {previewLoading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-soft)' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem', animation: 'spin 1s linear infinite' }}>⏳</div>
                  <div>Laddar förhandsvisning...</div>
                </div>
              ) : previewContent?.error ? (
                <div style={{ textAlign: 'center', color: 'var(--danger)', padding: '2rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
                  <div>{previewContent.error}</div>
                </div>
              ) : previewContent?.url ? (
                (() => {
                  const ext = previewContent.ext;
                  if (['jpg', 'jpeg', 'png'].includes(ext)) {
                    return (
                      <img 
                        src={previewContent.url} 
                        alt={previewDoc.namn}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%', 
                          objectFit: 'contain',
                          boxShadow: '0 4px 12px rgba(0,0,0,.15)'
                        }}
                      />
                    );
                  } else if (ext === 'pdf') {
                    // PDF - show options since iframe is blocked by CSP
                    return (
                      <div style={{ textAlign: 'center', color: 'var(--text-mid)', padding: '2rem' }}>
                        <div style={{ fontSize: '5rem', marginBottom: '1.5rem' }}>📕</div>
                        <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem', fontWeight: 600 }}>
                          {previewDoc.namn}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-soft)', marginBottom: '2rem' }}>
                          PDF-dokument
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => window.open(previewContent.url, '_blank')}
                            className="btn btn-forest"
                          >
                            🔍 Öppna i ny flik
                          </button>
                          <button
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = previewContent.url;
                              link.download = previewContent.filename || previewDoc.namn;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            className="btn btn-ghost"
                          >
                            ⬇ Ladda ner
                          </button>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div style={{ textAlign: 'center', color: 'var(--text-mid)', padding: '2rem' }}>
                        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📄</div>
                        <div style={{ marginBottom: '1rem' }}>Detta dokument kan inte förhandsvisas</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-soft)' }}>
                          Filtyp: {ext?.toUpperCase() || 'Okänd'}
                        </div>
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = previewContent.url;
                            link.download = previewContent.filename || previewDoc.namn;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className="btn btn-forest"
                          style={{ marginTop: '1rem' }}
                        >
                          ⬇ Ladda ner istället
                        </button>
                      </div>
                    );
                  }
                })()
              ) : null}
            </div>
          </div>
        </div>
      )}
      
      {/* Shares Modal */}
      {showShares && (
        <div style={{ 
          position: 'fixed', 
          inset: 0, 
          background: 'rgba(0,0,0,.5)', 
          zIndex: 300, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '1rem' 
        }}>
          <div style={{ 
            background: 'var(--white)', 
            borderRadius: 8, 
            width: '90vw', 
            maxWidth: 800,
            maxHeight: '80vh',
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,.4)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{ 
              padding: '1.25rem 1.5rem', 
              borderBottom: '1px solid var(--border)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              background: 'var(--forest)',
              color: '#fff'
            }}>
              <h3 style={{ 
                fontFamily: '"Playfair Display", serif', 
                fontSize: '1.2rem', 
                margin: 0 
              }}>
                🔗 Aktiva delningslänkar
              </h3>
              <button 
                onClick={() => setShowShares(false)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  fontSize: '1.5rem', 
                  color: '#fff',
                  padding: '0.25rem',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>
            
            {/* Content */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto',
              padding: '1.5rem'
            }}>
              {sharesLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔄</div>
                  <div style={{ color: 'var(--text-mid)' }}>Laddar delningslänkar...</div>
                </div>
              ) : shares.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem',
                  color: 'var(--text-mid)'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                  <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
                    Inga aktiva delningslänkar
                  </div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                    Du har inte skapat några delningslänkar ännu.
                    <br />
                    Välj ett dokument och klicka på "🔗" för att skapa en länk.
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: 'var(--text-soft)', 
                    marginBottom: '0.5rem',
                    fontWeight: 600 
                  }}>
                    {shares.length} aktiva länkar (sorterade efter skapandedatum)
                  </div>
                  {shares.map(share => {
                    const isPermanent = share.expiresAt === null;
                    const isExpired = !isPermanent && new Date(share.expiresAt) < new Date();
                    const daysLeft = isPermanent ? null : Math.ceil((new Date(share.expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <div 
                        key={share.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          padding: '1rem',
                          background: 'var(--cream)',
                          borderRadius: 6,
                          border: '1px solid var(--border)'
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontSize: '0.9rem', 
                            fontWeight: 600, 
                            color: 'var(--text)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginBottom: '0.25rem'
                          }}>
                            {share.documentName}
                          </div>
                          <div style={{ 
                            fontSize: '0.78rem', 
                            color: 'var(--text-soft)',
                            marginBottom: '0.25rem'
                          }}>
                            {share.documentCategory && (
                              <span style={{
                                display: 'inline-block',
                                padding: '0.1rem 0.4rem',
                                background: 'var(--forest)',
                                color: '#fff',
                                borderRadius: 4,
                                fontSize: '0.7rem',
                                marginRight: '0.5rem'
                              }}>
                                {share.documentCategory}
                              </span>
                            )}
                            Skapad: {new Date(share.createdAt).toLocaleDateString('sv-SE')}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-mid)' }}>
                            {isPermanent ? (
                              <span style={{ color: 'var(--forest)' }}>∞ Permanent</span>
                            ) : isExpired ? (
                              <span style={{ color: 'var(--danger)' }}>⏰ Utgången</span>
                            ) : (
                              <span style={{ color: 'var(--forest)' }}>
                                ⏰ Giltig i {daysLeft} dagar
                              </span>
                            )}
                            {share.usedCount > 0 && (
                              <span style={{ marginLeft: '1rem' }}>
                                📥 {share.usedCount} nedladdning{share.usedCount !== 1 ? 'ar' : ''}
                                {share.lastUsedAt && ` (senast ${new Date(share.lastUsedAt).toLocaleDateString('sv-SE')})`}
                              </span>
                            )}
                          </div>
                          <div style={{ 
                            fontSize: '0.72rem', 
                            color: 'var(--text-soft)', 
                            marginTop: '0.5rem',
                            fontFamily: 'monospace',
                            background: '#fff',
                            padding: '0.35rem 0.5rem',
                            borderRadius: 4,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {share.shareUrl}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                          <button
                            onClick={() => navigator.clipboard.writeText(share.shareUrl)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: 'var(--forest)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: '0.78rem'
                            }}
                          >
                            Kopiera
                          </button>
                          <button
                            onClick={() => handleDeleteShare(share.id)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: 'var(--danger)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: '0.78rem'
                            }}
                          >
                            Ta bort
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div style={{ 
              padding: '1rem 1.5rem', 
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              background: 'var(--white)'
            }}>
              <button 
                onClick={() => setShowShares(false)}
                className="btn btn-ghost"
              >
                Stäng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DocRow({ doc, onEdit, onDelete, onMove, onPreview, onShare, canEdit, isSelected, onSelect, selectionMode, isMobile }) {
  const [hov, setHov] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [moving, setMoving] = useState(false);
  const hasFile = doc.fil_url || doc.fil;
  const isLink = hasFile && hasFile !== '#';
  const isProtected = doc.is_protected || doc.isProtected;

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      const response = await ProtectedDocumentsAPI.download(doc.id);

      let blobUrl;
      let filename;

      if (response.url) {
        // Fetch file bytes directly from Supabase — avoids popup blocker + cross-origin download issues
        const fileRes = await fetch(response.url);
        if (!fileRes.ok) throw new Error(`Storage error: ${fileRes.status}`);
        const blob = await fileRes.blob();
        blobUrl = window.URL.createObjectURL(blob);
        filename = response.filename || doc.namn;
      } else if (response.file) {
        const byteArray = Uint8Array.from(atob(response.file), c => c.charCodeAt(0));
        const blob = new Blob([byteArray], { type: response.contentType });
        blobUrl = window.URL.createObjectURL(blob);
        filename = response.filename || doc.namn;
      }

      if (blobUrl) {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      }
    } catch (err) {
      console.error('Download failed:', err);
      alert('Kunde inte ladda ner filen. ' + (err.message || ''));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        gap: isMobile ? '0.6rem' : '0.9rem',
        padding: isMobile ? '0.75rem 0.9rem' : '0.85rem 1.1rem', 
        borderRadius: 5,
        border: `1px solid ${isSelected ? 'var(--forest)' : hov && isLink ? 'var(--forest)' : 'var(--border)'}`,
        background: isSelected ? 'rgba(44,74,46,.08)' : hov && isLink ? 'rgba(44,74,46,.03)' : 'var(--white)',
        transition: 'border-color .15s, background .15s',
        cursor: isLink && !selectionMode ? 'pointer' : 'default',
        opacity: downloading ? 0.7 : 1,
        flexDirection: isMobile ? 'column' : 'row'
      }}
    >
      {/* Mobile header row with checkbox, icon and filename */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: isMobile ? '0.6rem' : '0.9rem',
        width: '100%'
      }}>
        {/* Selection checkbox */}
        {selectionMode && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => { e.stopPropagation(); onSelect?.(doc.id); }}
            style={{
              width: isMobile ? '1.2rem' : '1.1rem',
              height: isMobile ? '1.2rem' : '1.1rem',
              cursor: 'pointer',
              accentColor: 'var(--forest)',
              flexShrink: 0
            }}
          />
        )}
        
        {/* File icon - clickable for preview */}
        <button
          onClick={(e) => { e.stopPropagation(); onPreview?.(doc); }}
          title="Förhandsvisa dokument"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.2rem',
            borderRadius: 4,
            transition: 'background .15s, transform .1s',
            flexShrink: 0
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(44,74,46,.08)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
          onMouseDown={e => e.currentTarget.style.transform = 'scale(0.95)'}
          onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <FileTypeBadge filename={doc.fil_namn || doc.fil} />
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: isMobile ? '0.95rem' : '0.92rem', 
            fontWeight: 500,
            color: hov && isLink ? 'var(--forest)' : 'var(--text)',
            overflow: isMobile ? 'visible' : 'hidden', 
            textOverflow: isMobile ? 'clip' : 'ellipsis', 
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            wordBreak: isMobile ? 'break-word' : 'normal',
            lineHeight: isMobile ? '1.4' : '1.2',
            transition: 'color .15s'
          }}>
            {doc.namn}
          </div>
        </div>
      </div>

      {/* Mobile: meta and actions row */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        width: '100%',
        marginTop: isMobile ? '0.4rem' : '0',
        paddingLeft: isMobile ? (selectionMode ? '2rem' : '0') : '0'
      }}>
        <div style={{ fontSize: isMobile ? '0.75rem' : '0.73rem', color: 'var(--text-soft)' }}>
          {fmtDate(doc.datum)}{doc.fil_storlek ? ` · ${fmtSize(doc.fil_storlek)}` : ''}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '0.25rem', 
        opacity: hov ? 1 : 0.6, 
        transition: 'opacity .15s',
        alignItems: 'center'
      }}>
        {/* Download button */}
        {isLink && (
          <button 
            onClick={handleDownload}
            disabled={downloading}
            title={downloading ? 'Laddar...' : 'Öppna'}
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: downloading ? 'wait' : 'pointer', 
              padding: '0.35rem 0.5rem', 
              borderRadius: 3, 
              color: hov ? 'var(--forest)' : 'var(--text-soft)', 
              display: 'flex', 
              alignItems: 'center',
              fontSize: '0.9rem',
              transition: 'color .15s'
            }}
          >
            {downloading ? '⏳' : '↗'}
          </button>
        )}
        
        {/* Move button (content creators can move) */}
        {canEdit && (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              if (moving) return;
              setMoving(true);
              try {
                await onMove(doc.id, !isProtected);
              } finally {
                setMoving(false);
              }
            }}
            disabled={moving}
            title={isProtected ? 'Gör publik' : 'Gör intern'}
            style={{
              background: 'none',
              border: 'none',
              cursor: moving ? 'wait' : 'pointer',
              padding: '0.35rem 0.5rem',
              borderRadius: 3,
              color: isProtected ? (hov ? 'var(--lake)' : 'var(--text-soft)') : (hov ? 'var(--forest)' : 'var(--text-soft)'),
              fontSize: '0.85rem',
              transition: 'color .15s',
              opacity: moving ? 0.5 : 1
            }}
          >
            {moving ? '⏳' : (isProtected ? '🔒→🌐' : '🌐→🔒')}
          </button>
        )}
        
        {/* Share button (content creators can share) */}
        {canEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onShare?.(doc); }}
            title="Skapa delningslänk"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.35rem 0.5rem',
              borderRadius: 3,
              color: hov ? 'var(--forest)' : 'var(--text-soft)',
              fontSize: '0.85rem',
              transition: 'color .15s'
            }}
          >
            🔗
          </button>
        )}
        
        {/* Edit/Delete buttons (only for content creators) */}
        {canEdit && (
          <>
            <div style={{ width: '1px', height: '1.2rem', background: 'var(--border)', margin: '0 0.25rem' }} />
            <button 
              onClick={(e) => { e.stopPropagation(); onEdit(doc); }} 
              title="Redigera"
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                padding: '0.35rem 0.5rem', 
                borderRadius: 3, 
                color: hov ? 'var(--lake)' : 'var(--text-soft)', 
                fontSize: '0.85rem',
                transition: 'color .15s'
              }}
            >
              ✏
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onDelete(doc.id, doc.namn); }} 
              title="Ta bort"
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                padding: '0.35rem 0.5rem', 
                borderRadius: 3, 
                color: hov ? 'var(--danger)' : 'var(--text-soft)', 
                fontSize: '0.85rem',
                transition: 'color .15s'
              }}
            >
              🗑
            </button>
          </>
        )}
      </div>
    </div>
  );
}
